// @ts-nocheck
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  IconBriefcase, IconFileDescription, IconFileInvoice, IconCalendar,
  IconChevronRight, IconBrandWhatsapp, IconPrinter, IconLoader2, IconMessageCircle,
  IconCoins, IconClock, IconFileText, IconCalendarEvent, IconMapPin, IconX,
} from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import Time from '@/utils/time';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import PageContext from '@/utils/page-context';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import DownloadService from '@/utils/download-service';
import FeedbackService from '@/services/feedback-service';
import {
  StatusPill, deriveStatusTone, IconPenSign,
  quoteStatusDisplay, invoiceStatusDisplay, MiniCard, MiniRow,
} from '../CustomerZoneShared';
import CustomerZoneMessageModal from '../CustomerZoneMessageModal';
import styles from '../CustomerZoneDetail.module.css';

const JOB_TABS = [
  { key: 'overview',     label: 'Overview',      Icon: IconBriefcase },
  { key: 'quotes',       label: 'Quotes',        Icon: IconFileDescription },
  { key: 'invoices',     label: 'Invoices',      Icon: IconFileInvoice },
  { key: 'appointments', label: 'Appointments',  Icon: IconCalendar },
];

function getInitials(name: string) {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function CustomerZoneJobDetail() {
  const router = useRouter();
  const { t, c, i } = router.query;
  const pageContext = useContext(PageContext) as any;
  const [printing, setPrinting] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [activeJobTab, setActiveJobTab] = useState('overview');
  const [disableAcceptReject, setDisableAcceptReject] = useState(false);
  const [confirmARAction, setConfirmARAction] = useState<null | 'accept' | 'decline'>(null);
  const [notification, setNotification] = useState<null | { msg: string; danger?: boolean }>(null);
  const notifTimer = useRef<any>(null);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const printMenuRef = useRef<any>(null);

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  const { data: jobData } = useQuery(
    ['czJob', t, c, i],
    () => Fetch.get({ url: '/CustomerZone/GetJob', params: { jobCardID: i }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!i }
  );

  const { data: statusChangesData } = useQuery(
    ['czJobStatusChanges', t, c, i],
    () => Fetch.get({ url: '/CustomerZone/GetJobStatusChanges', params: { jobCardID: i }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!i }
  );

  const jobInternalId = jobData?.ID;

  const { data: relatedQuotesData } = useQuery(
    ['czJobRelatedQuotes', t, c, jobInternalId],
    () => Fetch.post({
      url: '/CustomerZone/GetQuotes',
      params: {
        pageSize: 50, pageIndex: 0, PopulatedList: false,
        SortExpression: 'QuoteDate', SortDirection: 'descending',
        ItemId: jobInternalId, ModuleIDList: ['JobCard'],
        QuoteStatusIDList: ['Approved', 'Accepted', 'Declined', 'Invoiced', 'Expired', 'Cancelled'],
        IncludeClosed: true,
      },
      tenantID: t, customerID: c, apiUrlOverride: tenant.API,
    }),
    { enabled: !!tenant && !!jobInternalId }
  );

  const { data: relatedInvoicesData } = useQuery(
    ['czJobRelatedInvoices', t, c, jobInternalId],
    () => Fetch.post({
      url: '/CustomerZone/GetInvoices',
      params: {
        PageSize: 50, SortExpression: 'StartDate', SortDirection: 'descending', PopulatedList: false,
        ItemId: jobInternalId, ModuleIDList: ['JobCard'],
        InvoiceStatusIDList: ['Unpaid', 'Overdue', 'Paid'],
        IncludeClosed: true,
      },
      tenantID: t, customerID: c, apiUrlOverride: tenant.API,
    }),
    { enabled: !!tenant && !!jobInternalId }
  );

  const { data: relatedAppointmentsData } = useQuery(
    ['czJobRelatedAppointments', t, c, jobInternalId],
    () => Fetch.post({
      url: '/CustomerZone/GetAppointments',
      params: {
        PageSize: 50, PageIndex: 0, SortExpression: 'StartDateTime', SortDirection: 'ascending',
        ItemId: jobInternalId, ModuleIDList: ['JobCard'],
      },
      tenantID: t, customerID: c, apiUrlOverride: tenant.API,
    }),
    { enabled: !!tenant && !!jobInternalId }
  );

  const { data: documentData } = useQuery(
    ['czJobDocument', t, c],
    () => Fetch.get({ url: '/CustomerZone/Document', params: {}, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant }
  );

  const { data: ratingsData } = useQuery(
    ['czJobRatings', t, c, jobInternalId],
    () => FeedbackService.getFeedbackListCustomerZone(t, c, tenant.API, jobInternalId, Enums.Module.JobCard),
    { enabled: !!tenant && !!jobInternalId }
  );

  useEffect(() => {
    if (jobData?.headerTenantName) pageContext?.updateTenantName?.(jobData.headerTenantName);
  }, [jobData]);

  useEffect(() => {
    if (!showPrintMenu) return;
    const handler = (e: MouseEvent) => {
      if (printMenuRef.current && !printMenuRef.current.contains(e.target)) setShowPrintMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPrintMenu]);

  const applicableDocuments = useMemo(() => {
    if (!documentData) return null;
    if (documentData.legacyDocuments) return [];
    if (!documentData.documentDefinition) return [];
    try {
      const meta = JSON.parse(documentData.documentDefinition.MetaData);
      return (meta.JobDocuments || [])
        .filter((x: any) => x.CustomerZonePrintable && x.IsActive)
        .map((x: any) => ({ link: x.DocumentType, text: x.Title }));
    } catch {
      return [];
    }
  }, [documentData]);

  const isLegacyPrint = documentData?.legacyDocuments ?? true;

  const ratings: any[] = useMemo(() => {
    const data = ratingsData?.data || [];
    return data
      .filter((x: any) => x.Rating && x.CompletedDate)
      .sort((a: any, b: any) => new Date(b.CompletedDate).getTime() - new Date(a.CompletedDate).getTime());
  }, [ratingsData]);

  if (!tenant || !jobData) return null;

  const job = jobData;
  const statusChanges = statusChangesData?.Results || [];
  const relatedQuotes: any[] = relatedQuotesData?.Results || [];
  const relatedInvoices: any[] = relatedInvoicesData?.Results || [];
  const relatedAppointments: any[] = relatedAppointmentsData?.Results || [];

  const waPhone = company?.WhatsAppNumber
    ? company.WhatsAppNumber.replace(/\D/g, '').replace(/^0/, '27') : null;
  const base = `?t=${t}&c=${c}`;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';
  const currencySymbol = company?.Currency?.Symbol || '';
  const tone = deriveStatusTone(job.JobCardStatusDescription || '');
  const employees: any[] = job.Employees || job.AssignedEmployees || [];

  const contactName = job.Contact
    ? `${job.Contact.FirstName || ''} ${job.Contact.LastName || ''}`.trim()
    : job.CustomerName || '';

  const customerAssets: any[] = (job.JobInventory || [])
    .filter((x: any) => x.IsActive && x.StockItemStatus === Enums.StockItemStatus.WorkedOn)
    .sort((a: any, b: any) => a.LineNumber - b.LineNumber);

  const showNotification = (msg: string, danger = false) => {
    setNotification({ msg, danger });
    clearTimeout(notifTimer.current);
    notifTimer.current = setTimeout(() => setNotification(null), 5000);
  };

  const doAcceptReject = async (accept: boolean) => {
    setConfirmARAction(null);
    setDisableAcceptReject(true);
    try {
      await Fetch.get({
        url: '/CustomerZone/JobAcceptReject',
        params: { jobCardID: job.ID, accept },
        tenantID: t, customerID: c, apiUrlOverride: tenant.API,
      });
      showNotification(`${job.JobCardNumber} was ${accept ? 'accepted' : 'declined'} successfully.`);
    } catch {
      showNotification('Something went wrong. Please try again.', true);
    } finally {
      setDisableAcceptReject(false);
    }
  };

  const handleDownload = async () => {
    setPrinting(true);
    setShowPrintMenu(false);
    try {
      await DownloadService.downloadFile(
        'GET', `/CustomerZone/GetJobCustomerDocument?jobCardID=${job.ID}`,
        null, true, false, t, c, tenant.API, false
      );
    } finally {
      setPrinting(false);
    }
  };

  const exportDocument = async (documentType: string) => {
    setShowPrintMenu(false);
    setPrinting(true);
    try {
      await DownloadService.downloadFile(
        'GET', `/CustomerZone/GetJobDocument?jobCardID=${job.ID}&documentType=${documentType}`,
        null, true, false, t, c, tenant.API, false
      );
    } finally {
      setPrinting(false);
    }
  };

  const handleWhatsApp = () => {
    if (!waPhone) return;
    const msg = `Hi! This is ${job.CustomerName} reaching out about job ${job.JobCardNumber}.`;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const navToRelated = (path: string) => {
    const fromJobId = encodeURIComponent(job.ID);
    const fromJobNum = encodeURIComponent(job.JobCardNumber);
    router.push(`${path}&fromJobId=${fromJobId}&fromJobNum=${fromJobNum}`);
  };

  const hasSigned = !!job.IsSigned;

  const tabCounts: Record<string, number> = {
    quotes:       relatedQuotes.length,
    invoices:     relatedInvoices.length,
    appointments: relatedAppointments.length,
  };

  return (
    <>
      <div
        className={`${styles.topAlert}${notification ? ` ${styles.topAlertShow}` : ''}${notification?.danger ? ` ${styles.topAlertDanger}` : ''}`}
        aria-hidden={!notification}
        aria-live="polite"
      >
        <span className={styles.topAlertMsg}>{notification?.msg || ''}</span>
        <button className={styles.topAlertClose} onClick={() => setNotification(null)} aria-label="Dismiss">
          <IconX size={13} />
        </button>
      </div>

      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>
      <div className={styles.root}>
        <div className={styles.page}>
          <div className={styles.wrap}>
            <div className={styles.portalId}>
              <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} />
                ) : (
                  <span className={styles.portalBadgeFallback}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className={styles.portalName}>{companyName}</p>
            </div>
          <div className={styles.card}>
            <div className={styles.cardToolbar}>
              <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/customerzone" search={{ t, c }}>Home</Link>
                <span className={styles.bcSep}><IconChevronRight /></span>
                <Link to="/customerzone" search={{ t, c, tab: 'jobs', expand: '1' }}>Jobs</Link>
                <span className={styles.bcSep}><IconChevronRight /></span>
                <span className={styles.bcCurrent}>{job.JobCardNumber}</span>
              </nav>
            </div>

            <div className={styles.cardInner}>
              <div className={styles.detail}>

                <div className={styles.detailHeader}>
                  <h1 className={styles.dId}>{job.JobCardNumber}</h1>
                  <div className={styles.dActions}>
                    {job.ShowAcceptReject && (
                      <>
                        <button
                          className={`${styles.act} ${styles.actDecline}`}
                          type="button"
                          disabled={disableAcceptReject}
                          onClick={() => setConfirmARAction('decline')}
                        >
                          Decline
                        </button>
                        <button
                          className={`${styles.act} ${styles.actAccept}`}
                          type="button"
                          disabled={disableAcceptReject}
                          onClick={() => setConfirmARAction('accept')}
                        >
                          Accept
                        </button>
                      </>
                    )}
                    {waPhone && (
                      <button className={styles.act} onClick={handleWhatsApp}>
                        <IconBrandWhatsapp size={14} color="#25d366" /><span>WhatsApp</span>
                      </button>
                    )}
                    <button className={`${styles.act} ${styles.actIconOnly}`} type="button" onClick={() => setShowMessage(true)} title="Leave a comment">
                      <IconMessageCircle size={14} />
                    </button>
                    {isLegacyPrint || !applicableDocuments || applicableDocuments.length <= 1 ? (
                      <button
                        className={styles.act}
                        type="button"
                        disabled={printing || (applicableDocuments !== null && applicableDocuments.length === 0)}
                        title={applicableDocuments?.length === 0 ? 'Documents not available' : undefined}
                        onClick={applicableDocuments?.length === 1 ? () => exportDocument(applicableDocuments[0].link) : handleDownload}
                      >
                        {printing ? <IconLoader2 size={14} className={styles.actSpinner} /> : <IconPrinter size={14} />}
                        <span>{applicableDocuments?.length === 1 ? applicableDocuments[0].text : 'Print'}</span>
                      </button>
                    ) : (
                      <div style={{ position: 'relative' }} ref={printMenuRef}>
                        <button
                          className={styles.act}
                          type="button"
                          disabled={printing}
                          onClick={() => setShowPrintMenu(v => !v)}
                        >
                          {printing ? <IconLoader2 size={14} className={styles.actSpinner} /> : <IconPrinter size={14} />}
                          <span>Print</span>
                          <svg style={{ marginLeft: 2 }} width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none"/></svg>
                        </button>
                        {showPrintMenu && (
                          <div style={{
                            position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20,
                            background: '#fff', border: '1px solid var(--border-grey-color)',
                            borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                            minWidth: 160, padding: '4px 0',
                          }}>
                            {applicableDocuments.map((doc: any) => (
                              <button
                                key={doc.link}
                                type="button"
                                style={{
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '8px 14px', background: 'none', border: 'none',
                                  fontSize: 13, cursor: 'pointer', color: 'var(--dark-primary-color)',
                                  fontFamily: 'inherit',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fb')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                onClick={() => exportDocument(doc.link)}
                              >
                                {doc.text}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <Link
                      className={`${styles.act} ${styles.actSign}`}
                      to="/customerzone/signature"
                      search={{ t, c, i: job.ID }}
                    >
                      <IconPenSign size={14} /><span>{hasSigned ? 'Re-sign' : 'Sign'}</span>
                    </Link>
                  </div>
                </div>

                {/* Job inner tabs: Overview | Quotes | Invoices | Appointments */}
                <div className={styles.jobTabs} role="tablist">
                  {JOB_TABS.map(({ key, label, Icon }) => (
                    <button
                      key={key}
                      className={`${styles.jobTab}${activeJobTab === key ? ` ${styles.jobTabActive}` : ''}`}
                      role="tab"
                      aria-selected={activeJobTab === key}
                      onClick={() => setActiveJobTab(key)}
                    >
                      <Icon size={15} />
                      <span>{label}</span>
                      {key !== 'overview' && (
                        <span className={styles.jobTabCount}>{tabCounts[key] ?? 0}</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Overview pane */}
                {activeJobTab === 'overview' && (
                  <div className={styles.jobPane}>
                    <div className={styles.contact}>
                      {contactName && <span className={styles.cName}>{contactName}</span>}
                      {job.CustomerName && contactName !== job.CustomerName && (
                        <span className={styles.cLine}>{job.CustomerName}</span>
                      )}
                      {job.Contact?.MobileNumber && <span className={styles.cLine}>{job.Contact.MobileNumber}</span>}
                      {job.Contact?.EmailAddress && <span className={styles.cLine}>{job.Contact.EmailAddress}</span>}
                      {job.Location?.LocationDisplay && <span className={styles.cLine}>{job.Location.LocationDisplay}</span>}
                    </div>

                    <div className={styles.panel}>
                      <div className={styles.panelHead}>
                        <h2 className={styles.panelTitle}>Job Detail</h2>
                        <span className={styles.panelBadge}>
                          <StatusPill text={job.JobCardStatusDescription || '—'} tone={tone} />
                        </span>
                      </div>
                      <div className={styles.fields}>
                        {job.StartDate && (
                          <div className={styles.field}>
                            <span className={styles.fLabel}>Start Date</span>
                            <span className={styles.fValue}>{Time.toISOString(Time.parseDate(job.StartDate), false, false)}</span>
                          </div>
                        )}
                        {job.EndDate && (
                          <div className={styles.field}>
                            <span className={styles.fLabel}>End Date</span>
                            <span className={styles.fValue}>{Time.toISOString(Time.parseDate(job.EndDate), false, false)}</span>
                          </div>
                        )}
                        {(job.JobTypeName || job.JobType) && (
                          <div className={styles.field}>
                            <span className={styles.fLabel}>Job Type</span>
                            <span className={styles.fValue}>{job.JobTypeName || job.JobType}</span>
                          </div>
                        )}
                        {job.Description && (
                          <div className={`${styles.field} ${styles.fieldWide}`}>
                            <span className={styles.fLabel}>Description</span>
                            <span className={styles.fValue} style={{ whiteSpace: 'pre-wrap' }}>{job.Description}</span>
                          </div>
                        )}
                      </div>

                      {customerAssets.length > 0 && (
                        <>
                          <hr className={styles.divider} />
                          <div className={styles.section}>
                            <h3 className={styles.subhead}>Customer Assets</h3>
                            <div className={styles.tableWrap}>
                              <table className={styles.tbl}>
                                <thead>
                                  <tr>
                                    <th>Code</th>
                                    <th>Description</th>
                                    <th>Quantity</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customerAssets.map((item: any, idx: number) => (
                                    <tr key={idx}>
                                      <td data-label="Code">{item.ProductID ? item.ProductNumber : item.InventoryCode}</td>
                                      <td data-label="Description">{item.InventoryDescription}</td>
                                      <td data-label="Quantity">{item.QuantityRequested}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}

                      {employees.length > 0 && (
                        <>
                          <hr className={styles.divider} />
                          <div className={styles.section}>
                            <h3 className={styles.subhead}>Assigned Employees</h3>
                            <div className={styles.chipsWrap}>
                              {employees.map((emp: any, idx: number) => {
                                const name = emp.FullName || emp.Name || `${emp.FirstName || ''} ${emp.LastName || ''}`.trim() || '?';
                                return (
                                  <span key={idx} className={styles.chip}>
                                    <span className={styles.chipAvatar}>{getInitials(name)}</span>
                                    <span className={styles.chipName}>{name}</span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {statusChanges.length > 0 && (
                      <div className={styles.panel}>
                        <h2 className={styles.panelTitle}>Summary</h2>
                        <div className={styles.tableWrap}>
                          <table className={styles.tbl}>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statusChanges.map((sc: any, idx: number) => (
                                <tr key={idx}>
                                  <td data-label="Date">
                                    {Time.toISOString(sc.StatusChangedDate || sc.CreatedDate, false)}
                                  </td>
                                  <td className="strong" data-label="Description">
                                    {sc.JobCardStatusDescription}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {ratings.length > 0 && (
                      <div className={styles.panel}>
                        <h2 className={styles.panelTitle}>Job Rating</h2>
                        <div className={styles.tableWrap}>
                          <table className={styles.tbl}>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Rating</th>
                                <th>Comment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ratings.map((item: any, idx: number) => (
                                <tr key={idx}>
                                  <td data-label="Date" style={{ whiteSpace: 'nowrap' }}>
                                    {Time.toISOString(item.CompletedDate, false)}
                                  </td>
                                  <td data-label="Rating" style={{ whiteSpace: 'nowrap', color: '#f59e0b', letterSpacing: 2 }}>
                                    {'★'.repeat(item.Rating)}{'☆'.repeat(5 - item.Rating)}
                                  </td>
                                  <td data-label="Comment">{item.Comment || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Quotes pane */}
                {activeJobTab === 'quotes' && (
                  <div className={styles.jobPane}>
                    {relatedQuotes.length === 0 ? (
                      <p className={styles.jobEmpty}>No quotes for this job yet</p>
                    ) : (
                      <div className={styles.jobGrid}>
                        {relatedQuotes.map((q: any, idx: number) => {
                          const { label, tone: qTone } = quoteStatusDisplay(q.QuoteStatus);
                          const href = `/customerzone/viewquote${base}&i=${q.ID}`;
                          return (
                            <MiniCard
                              key={idx}
                              href={href}
                              title={q.QuoteNumber}
                              tone={qTone}
                              statusText={label}
                              onClick={() => navToRelated(`/customerzone/viewquote${base}&i=${q.ID}`)}
                              rows={<>
                                {q.ItemReference && (
                                  <MiniRow icon={<IconFileText />}>Ref {q.ItemReference}</MiniRow>
                                )}
                                {q.ExpiryDate && (
                                  <MiniRow icon={<IconClock />}>
                                    {Time.toISOString(Time.parseDate(q.ExpiryDate), false, false)}
                                  </MiniRow>
                                )}
                                <MiniRow icon={<IconCoins />}>
                                  <b>{Helper.getCurrencyValue(q.TotalInclusive, currencySymbol)}</b>
                                </MiniRow>
                              </>}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Invoices pane */}
                {activeJobTab === 'invoices' && (
                  <div className={styles.jobPane}>
                    {relatedInvoices.length === 0 ? (
                      <p className={styles.jobEmpty}>No invoices for this job yet</p>
                    ) : (
                      <div className={styles.jobGrid}>
                        {relatedInvoices.map((inv: any, idx: number) => {
                          const { label, tone: invTone } = invoiceStatusDisplay(inv.InvoiceStatus);
                          const href = `/customerzone/viewinvoice${base}&i=${inv.ID}`;
                          return (
                            <MiniCard
                              key={idx}
                              href={href}
                              title={inv.InvoiceNumber}
                              tone={invTone}
                              statusText={label}
                              onClick={() => navToRelated(`/customerzone/viewinvoice${base}&i=${inv.ID}`)}
                              rows={<>
                                {inv.ItemReference && (
                                  <MiniRow icon={<IconFileText />}>Ref {inv.ItemReference}</MiniRow>
                                )}
                                {inv.DueDate && (
                                  <MiniRow icon={<IconClock />}>
                                    {Time.toISOString(Time.parseDate(inv.DueDate), false, false)}
                                  </MiniRow>
                                )}
                                <MiniRow icon={<IconCoins />}>
                                  <b>{Helper.getCurrencyValue(inv.TotalInclusive, currencySymbol)}</b>
                                </MiniRow>
                              </>}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Appointments pane */}
                {activeJobTab === 'appointments' && (
                  <div className={styles.jobPane}>
                    {relatedAppointments.length === 0 ? (
                      <p className={styles.jobEmpty}>No appointments for this job yet</p>
                    ) : (
                      <div className={styles.jobGrid}>
                        {relatedAppointments.map((appt: any, idx: number) => {
                          const apptId = appt.ID || appt.AppointmentID;
                          const href = `/customerzone/viewappointment${base}&i=${apptId}`;
                          return (
                            <MiniCard
                              key={idx}
                              href={href}
                              title={appt.Subject || Time.formatDate(appt.StartDateTime)}
                              tone="info"
                              onClick={() => navToRelated(`/customerzone/viewappointment${base}&i=${apptId}`)}
                              rows={<>
                                <MiniRow icon={<IconCalendarEvent />}>{Time.formatDate(appt.StartDateTime)}</MiniRow>
                                <MiniRow icon={<IconClock />}>
                                  {Time.toISOString(Time.parseDate(appt.StartDateTime), false, true, false, null, false)}
                                </MiniRow>
                                {appt.Location?.AddressLine1 && (
                                  <MiniRow icon={<IconMapPin />}>{appt.Location.AddressLine1}</MiniRow>
                                )}
                              </>}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>

          <div className={styles.support}>
            <span className={styles.sTitle}>Need support?</span>
            <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
          </div>
          </div>{/* wrap */}
        </div>{/* page */}
      </div>{/* root */}

      {showMessage && (
        <CustomerZoneMessageModal
          module={Enums.Module.JobCard}
          itemID={job.ID}
          t={t} c={c} tenant={tenant}
          onClose={() => setShowMessage(false)}
        />
      )}

      {confirmARAction && (
        <div
          className={styles.modalScrim}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmARAction(null); }}
        >
          <div className={styles.modal} role="dialog" aria-modal="true">
            <p className={styles.modalTitle}>
              {confirmARAction === 'accept' ? 'Accept this job?' : 'Decline this job?'}
            </p>
            <p className={styles.modalBody}>
              {confirmARAction === 'accept'
                ? `You're about to accept ${job.JobCardNumber}. The company will be notified.`
                : `You're about to decline ${job.JobCardNumber}. The company will be notified.`}
            </p>
            <div className={styles.modalActions}>
              <button className={styles.act} type="button" onClick={() => setConfirmARAction(null)}>
                Cancel
              </button>
              <button
                className={`${styles.act} ${confirmARAction === 'accept' ? styles.actPrimary : styles.actOutlineDanger}`}
                type="button"
                onClick={() => doAcceptReject(confirmARAction === 'accept')}
              >
                {confirmARAction === 'accept' ? 'Accept' : 'Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
